import * as Sentry from "@sentry/react";
import { serializeError } from "serialize-error";

import { fetchWithAuth } from "./fetch";
import { isDevelopment } from "./utils";

type RemoteLogOptions = {
  /**
   * Should the error be logged to Doctor Portal backend.
   */
  remoteLog?: boolean;
  /**
   * Should the error be logged to Sentry.
   */
  sentryLog?: boolean;
};

/**
 * Sends an error to the server.
 * @param {unknown} error Exception, error object, or message.
 * @param {string} component Name of component.
 */
export function remoteLog(
  error: unknown,
  component: string,
  { remoteLog = true, sentryLog = true }: RemoteLogOptions = {},
): void {
  // Public plan URLs contain the bearer token and must not enter telemetry.
  if (window.location.pathname.startsWith("/viewer/plans/")) return;
  if (sentryLog) {
    sentryCaptureError(error, component);
  }

  if (remoteLog) {
    const url = window.location.pathname + window.location.search;

    fetchWithAuth(`${process.env.API}/remote/log`, {
      method: "POST",
      body: JSON.stringify({
        content: serializeError(error),
        component,
        url,
        app_version: process.env.APP_VERSION,
      }),
    }).catch((err) => {
      if (shouldLogError(err)) {
        sentryCaptureError(err, "remoteLog");
      }
    });
  }

  if (isDevelopment) {
    console.error("remoteLog()", component, error);
  }
}

function sentryCaptureError(error: unknown, component: string): void {
  if (error instanceof Error) {
    Sentry.captureException(error, { tags: { component } });
  } else {
    Sentry.captureMessage(String(error), { tags: { component } });
  }
}

/**
 * Checks if an error should be logged based on its message.
 */
export function shouldLogError(err: unknown): boolean {
  return !(
    err instanceof Error &&
    err.name == "TypeError" &&
    ["Failed to fetch", "Load failed"].includes(err.message)
  );
}
